import { BerthCall } from 'src/types/ViewModel/BerthCall';
import { ReportLocationEnum } from 'src/enums/reportLocationEnum';
import { RotationTreeEnum } from 'src/enums/rotationTreeEnum';
import { emptyMoveActionMsg } from './moveActionMsgUtils';
import { CargoOperation } from 'src/types/ViewModel/CargoOperation';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { setCargoOperationDetails } from 'src/factory/ReportEvents/Rotation/CargoOperationFactory';
import { CargoOperationTypeEnum } from 'src/enums/cargoOperationTypeEnum';
import { getCargoColor } from '../../cargoColorsUtils';
import { getChartererName, getFormattedCargoNumber } from '../../cargoOperationUtils';
import { isManualCall } from '../../stowageRotationUtils';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { Product } from 'src/types/ViewModel/Stowage/Product/Product';

export const mapCargoOperationToRotationCargoOperationSimple = (
    berthCall: RotationBerthCall,
    cargoOperation: CargoOperation
): RotationCargoOperation => {
    const voyageCargoNumberSplit = cargoOperation.cargoNumber.split('-');
    const voyageNumber = voyageCargoNumberSplit[0] || '';
    const cargoNumberStr = voyageCargoNumberSplit[1] || '';
    const rotationCargoOperation = {
        cargoOperationGuid: crypto.randomUUID(),
        imosCargoOperationId: cargoOperation.cargoOperationId,
        imosCargoId: cargoOperation.cargoId,
        cargo: {
            imosCargoId: cargoOperation.imosCargoId,
            productId: cargoOperation.productId,
            voyageNumber: voyageNumber,
            cargoNumber: +cargoNumberStr,
            product: {
                tradeName: cargoOperation.tradeName,
                chartererName: cargoOperation.chartererName,
            } as Product,
        } as RotationCargo,
        billOfLadingWeight: cargoOperation.billOfLadingWeight,
        nominatedWeight: cargoOperation.nominatedWeight,
        demurrageRatePerDay: cargoOperation.demurrageRatePerDay,
        pumpingRatePerHour: cargoOperation.pumpingRatePerHour,
        cpDate: cargoOperation.cpDate,
        operationType: cargoOperation.cargoOperationType === CargoOperationTypeEnum.Loading ? 'Loading' : 'Unloading',
    } as RotationCargoOperation;

    rotationCargoOperation.berthCall = berthCall;
    return rotationCargoOperation;
};

/**
 * Maps RotationCargoOperations to CargoOperations
 */
export const mapRotationCargoOperationsToCargoOperations = (
    rotationCargoOperations: RotationCargoOperation[],
    berthCall: BerthCall
): CargoOperation[] => {
    return rotationCargoOperations.map((bc) => mapRotationCargoOperationToCargoOperation(bc, berthCall));
};

/**
 * Maps a RotationCargoOperation to a CargoOperation
 */
const mapRotationCargoOperationToCargoOperation = (rotationCargoOperation: RotationCargoOperation, berthCall: BerthCall): CargoOperation => {
    const operationType = getOperationType(rotationCargoOperation.operationType);
    const displayWeight = Math.abs(
        rotationCargoOperation.billOfLadingWeight > 0 ? rotationCargoOperation.billOfLadingWeight : rotationCargoOperation.nominatedWeight ?? 0
    );

    const details = setCargoOperationDetails(operationType, displayWeight, rotationCargoOperation.cargo.product.tradeName);
    const formattedCargoNumber = getFormattedCargoNumber(
        rotationCargoOperation.cargo.voyageNumber,
        rotationCargoOperation.cargo.cargoNumber,
        isManualCall(rotationCargoOperation)
    );
    const name = `${formattedCargoNumber} ${details}`;
    const chartererName = getChartererName(rotationCargoOperation.cargo).trimEnd();
    return {
        cargoOperationId: rotationCargoOperation.imosCargoOperationId,
        id: rotationCargoOperation.imosCargoOperationId,
        cargoOperationType: operationType,
        isLoading: operationType === CargoOperationTypeEnum.Loading,
        isUnloading: operationType === CargoOperationTypeEnum.Unloading,
        nominatedWeight: Math.abs(rotationCargoOperation.nominatedWeight),
        billOfLadingWeight: Math.abs(rotationCargoOperation.billOfLadingWeight),
        displayWeight: displayWeight,
        productId: rotationCargoOperation.cargo.productId,
        cargoNumber: getFormattedCargoNumber(
            rotationCargoOperation.cargo.voyageNumber,
            rotationCargoOperation.cargo.cargoNumber,
            isManualCall(rotationCargoOperation)
        ),
        cargoId: rotationCargoOperation.imosCargoId,
        imosCargoId: rotationCargoOperation.imosCargoId,
        tradeName: rotationCargoOperation.cargo.product.tradeName,
        chartererName: chartererName,
        isTransshipmentOperation: null,
        name: name,
        dropDownName: `${name} (${chartererName})`,
        details: details,
        berthCall: berthCall,
        colorRgb: getCargoColor(rotationCargoOperation.cargo),
        nodeStatus: '',
        isDeleted: false,
        isSelected: false,
        isMoved: false,
        isLocked: false,
        actionMsg: emptyMoveActionMsg,
        reportLocation: ReportLocationEnum.Port,
        type: RotationTreeEnum.CargoOperation,
        portCall: berthCall.portCall,
        isMovedTo: false,
        movedFromClicked: false,
        delayInputWarningText: null,
        firstTimeInPortCall: null,
        deletedIconTooltipText: null,
        timezoneWarningText: null,
        portClusterInitialPortId: null,
        portClusterInitialPortName: null,
        imosPortFunction: null,
        aolDate: null,
        demurrageRatePerDay: rotationCargoOperation.demurrageRatePerDay,
        pumpingRatePerHour: rotationCargoOperation.pumpingRatePerHour,
        cpDate: rotationCargoOperation.cpDate,
    };
};

const getOperationType = (operationType: string): CargoOperationTypeEnum => {
    if (operationType === 'Loading') {
        return CargoOperationTypeEnum.Loading;
    }
    if (operationType === 'Unloading') {
        return CargoOperationTypeEnum.Unloading;
    }
    // TODO: RETURN-TYPE - Function can return undefined if operationType doesn't match 'Loading' or 'Unloading', but return type is CargoOperationTypeEnum (not undefined). Should handle default case or update return type.
};
